import { useState } from "react";
import { useFetch, apiPost, fmtDate } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { DemoTag } from "@/components/shieldx/Badges";
import { useAuth } from "@/context/AuthContext";
import { OctagonAlert, Ban, ShieldOff, Snowflake, RotateCcw, Zap, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CONTROLS = [
  { key: "EMERGENCY_STOP", label: "Emergency Stop", icon: Zap, desc: "Halt all automated jobs and require human approval." },
  { key: "ISOLATE", label: "Isolate", icon: Ban, desc: "Network-isolate an asset to stop lateral movement." },
  { key: "REVOKE", label: "Revoke", icon: ShieldOff, desc: "Revoke active sessions and API tokens." },
  { key: "FREEZE", label: "Freeze", icon: Snowflake, desc: "Freeze deployments and configuration changes." },
  { key: "RECOVER", label: "Recover", icon: RotateCcw, desc: "Begin validated recovery from trusted backups." },
];
const CONFIRM = "CONFIRM EMERGENCY";

export default function Emergency() {
  const { user } = useAuth();
  const actions = useFetch("/emergency/actions");
  const recovery = useFetch("/recovery-points");
  const [active, setActive] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [target, setTarget] = useState("Organization-wide");
  const [busy, setBusy] = useState(false);

  const allowed = ["super_admin", "org_owner", "incident_responder"].includes(user?.role);

  const execute = async () => {
    setBusy(true);
    try {
      await apiPost("/emergency/execute", { control: active.key, confirm: phrase, target });
      toast.success(`${active.label} executed`); setActive(null); setPhrase(""); actions.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execution failed");
    } finally { setBusy(false); }
  };

  const restore = async (id) => {
    try { const r = await apiPost(`/recovery-points/${id}/restore`, {}); toast.success(r.message || "Restore started"); recovery.reload(); }
    catch { toast.error("Restore failed"); }
  };

  if (actions.loading || recovery.loading) return <Loader label="Loading emergency control plane" />;

  return (
    <div>
      <PageHeader icon={OctagonAlert} title="Emergency Control Plane"
        subtitle="An independent control plane, separated from normal AI-agent permissions. Requires strong authority and dual confirmation."
        actions={<DemoTag />} />

      {!allowed && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
          <Lock className="h-4 w-4" /> Your role ({user?.role}) has read-only access. Critical controls require elevated authority.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CONTROLS.map((c) => (
          <div key={c.key} className="rounded-xl border border-red-500/20 bg-gradient-to-br from-red-500/5 to-slate-900/40 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-red-500/30 bg-red-500/10 text-red-400"><c.icon className="h-5 w-5" /></span>
            <h3 className="mt-4 font-display text-lg font-semibold text-slate-100">{c.label}</h3>
            <p className="mt-1 text-sm text-slate-400">{c.desc}</p>
            <button disabled={!allowed} onClick={() => { setActive(c); setPhrase(""); }} data-testid={`emergency-${c.key.toLowerCase()}`}
              className="mt-4 w-full rounded-lg border border-red-500/40 bg-red-500/10 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40">
              Activate
            </button>
          </div>
        ))}
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <ShieldCheck className="h-5 w-5 text-blue-400" />
          <h3 className="mt-3 font-display text-base font-semibold text-slate-100">Self-Protection</h3>
          <p className="mt-1 text-sm text-slate-400">The ShieldX AI can never disable or modify this control plane, grant itself privileges, or delete immutable logs.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel title="Recovery Points" desc="Immutable & trusted — compromised configs are never blindly restored">
          <div className="space-y-2">
            {recovery.data.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">{r.asset_name}</p>
                  <p className="font-mono text-[11px] text-slate-500">{r.type} · {r.size} · {r.integrity}</p>
                </div>
                <button onClick={() => restore(r.id)} data-testid={`restore-${r.id}`}
                  className="shrink-0 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2.5 py-1 text-xs font-medium text-indigo-300 hover:bg-indigo-500/20">Restore</button>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Emergency Action Log" desc="Every control action is audited">
          <div className="space-y-2">
            {actions.data.length === 0 && <p className="text-sm text-slate-500">No emergency actions executed yet.</p>}
            {actions.data.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5">
                <div className="flex items-center justify-between">
                  <span className="rounded border border-red-500/30 bg-red-500/10 px-2 py-0.5 font-mono text-[10px] text-red-300">{a.control}</span>
                  <span className="text-[11px] text-slate-500">{fmtDate(a.executed_at)}</span>
                </div>
                <p className="mt-1.5 text-sm text-slate-300">{a.target}</p>
                <p className="font-mono text-[11px] text-slate-500">by {a.actor}</p>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="border-red-500/30 bg-slate-900 text-slate-100">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-red-300"><active.icon className="h-5 w-5" /> {active.label}</DialogTitle>
                <DialogDescription className="text-slate-400">{active.desc} This action is logged to the tamper-evident ledger.</DialogDescription>
              </DialogHeader>
              <label className="text-xs font-medium text-slate-400">Target</label>
              <input value={target} onChange={(e) => setTarget(e.target.value)} data-testid="emergency-target"
                className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-red-500/50" />
              <label className="text-xs font-medium text-slate-400">Type <span className="font-mono text-red-300">{CONFIRM}</span> to confirm</label>
              <input value={phrase} onChange={(e) => setPhrase(e.target.value)} data-testid="emergency-confirm-input" placeholder={CONFIRM}
                className="h-10 w-full rounded-lg border border-slate-800 bg-slate-900/70 px-3 font-mono text-sm text-slate-100 outline-none focus:border-red-500/50" />
              <button onClick={execute} disabled={busy || phrase !== CONFIRM} data-testid="emergency-execute"
                className="mt-2 rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40">
                Execute {active.label}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
